import { Dimensions } from '../Measurement'
import { useState, useMemo, useEffect, useRef } from 'react'
import { RotateCw, Lock, CheckCircle2, Play, Pause } from 'lucide-react'
import { useTheme } from '../../context/ThemeContext'

interface ProductRotation3DPreviewProps {
    length: number | null
    width: number | null
    height: number | null
    unit?: string
    color?: string
    rotatable: boolean
    allowRotationX: boolean
    allowRotationY: boolean
    allowRotationZ: boolean
    onToggleAxis?: (axis: 'allow_rotation_x' | 'allow_rotation_y' | 'allow_rotation_z', val: boolean) => void
}

// ── Helper to adjust color brightness ────────────────────────────────────────
function adjustBrightness(hexColor: string, factor: number): string {
    let hex = (hexColor || '#6366f1').replace('#', '')
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('')
    const r = parseInt(hex.substring(0, 2), 16) || 99
    const g = parseInt(hex.substring(2, 4), 16) || 102
    const b = parseInt(hex.substring(4, 6), 16) || 241

    const nr = Math.min(255, Math.max(0, Math.round(r * factor)))
    const ng = Math.min(255, Math.max(0, Math.round(g * factor)))
    const nb = Math.min(255, Math.max(0, Math.round(b * factor)))

    return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`
}

// ── Single Axis 3D Preview Card ──────────────────────────────────────────────
interface AxisCardProps {
    axis: 'X' | 'Y' | 'Z'
    title: string
    subtitle: string
    description: string
    enabled: boolean
    rotatable: boolean
    l: number
    w: number
    h: number
    unit: string
    color: string
    onToggle: () => void
}

function SingleAxis3DCard({
    axis,
    title,
    description,
    enabled,
    rotatable,
    l,
    w,
    h,
    unit,
    color,
    onToggle,
}: AxisCardProps) {
    const [angleDeg, setAngleDeg] = useState(0) // 0 to 90 degrees
    const [isPlaying, setIsPlaying] = useState(false)
    const animRef = useRef<number | null>(null)
    const { theme } = useTheme()


    const isAxisActive = rotatable && enabled

    // Convention métier (X=Longitudinal, Y=Latéral, Z=Vertical) alignée sur Rotation.applyTo()
    const rotatedDims = useMemo(() => {
        if (axis === 'X') {
            // Rotation autour de X (longitudinal) => bascule sur le flanc
            // Échange Largeur (W) ↔ Hauteur (H)
            return { l: l, w: h, h: w }
        } else if (axis === 'Y') {
            // Rotation autour de Y (latéral) => bascule avant/arrière
            // Échange Longueur (L) ↔ Hauteur (H)
            return { l: h, w: w, h: l }
        } else {
            // Rotation autour de Z (vertical) => pivotement horizontal à plat
            // Échange Longueur (L) ↔ Largeur (W)
            return { l: w, w: l, h: h }
        }
    }, [axis, l, w, h])

    // Animation Loop
    useEffect(() => {
        if (isPlaying && isAxisActive) {
            let startTime: number | null = null
            const duration = 1200 // 1.2 seconds for full 90 deg loop

            const step = (timestamp: number) => {
                if (!startTime) startTime = timestamp
                const progress = ((timestamp - startTime) % duration) / duration
                // Smooth sine ping-pong between 0 and 90 deg
                const sinVal = (Math.sin(progress * Math.PI * 2 - Math.PI / 2) + 1) / 2
                setAngleDeg(sinVal * 90)
                animRef.current = requestAnimationFrame(step)
            }

            animRef.current = requestAnimationFrame(step)
        } else {
            if (animRef.current) cancelAnimationFrame(animRef.current)
            setAngleDeg(0)
        }

        return () => {
            if (animRef.current) cancelAnimationFrame(animRef.current)
        }
    }, [isPlaying, isAxisActive])

    // Axis Color Scheme — identique à l'OptimizationViewer (X=rouge, Y=vert, Z=bleu)
    const axisColorHex = axis === 'X' ? '#ef4444' : axis === 'Y' ? '#22c55e' : '#3b82f6'

    // ── 3D SVG Projection Geometry ──────────────────────────────────────────────
    const svgWidth = 220
    const svgHeight = 160
    const cx = svgWidth / 2
    const cy = svgHeight / 2 + 10

    // Calculate box scale to fit nicely in 220x160 canvas
    const maxDim = Math.max(l, w, h) || 100
    const scale = (Math.min(svgWidth, svgHeight) / maxDim) * 0.38

    // Base angles for camera
    const camYaw = 40 * (Math.PI / 180)
    const camPitch = 25 * (Math.PI / 180)

    // Current rotation angle of the box in radians
    const currentRad = (angleDeg * Math.PI) / 180

    // Project a static 3D point (Camera transform only, fixed in space)
    const projectStatic = (x: number, y: number, z: number): [number, number, number] => {
        const xCam = x * Math.cos(camYaw) - y * Math.sin(camYaw)
        const yCam = x * Math.sin(camYaw) + y * Math.cos(camYaw)
        const zCam = z

        const sx = cx + xCam * scale
        const sy = cy - (zCam * Math.cos(camPitch) - yCam * Math.sin(camPitch)) * scale
        const depth = yCam * Math.cos(camPitch) + zCam * Math.sin(camPitch)

        return [sx, sy, depth]
    }

    // Project a rotated 3D point (Rotates box around the specific axis)
    // Convention identique à Optimization3DViewer (Three.js):
    //   Dans notre espace SVG: x=L (longitudinal), y=W (latéral sol), z=H (vertical)
    //   Correspond à Three.js: X=x, Z=y (Three), Y=z (notre)
    const project3D = (x: number, y: number, z: number): [number, number, number] => {
        let rx = x
        let ry = y
        let rz = z

        if (axis === 'X') {
            // Axe X Métier (Longitudinal)
            // Bascule sur le côté (Roll) : tourne dans le plan Y-Z métier
            // Échange Largeur (W / y SVG) ↔ Hauteur (H / z SVG)
            rx = x
            ry = y * Math.cos(currentRad) - z * Math.sin(currentRad)
            rz = y * Math.sin(currentRad) + z * Math.cos(currentRad)
        } else if (axis === 'Y') {
            // Axe Y Métier (Latéral)
            // Bascule avant/arrière (Pitch) : tourne dans le plan X-Z métier
            // Échange Longueur (L / x SVG) ↔ Hauteur (H / z SVG)
            rx = x * Math.cos(currentRad) - z * Math.sin(currentRad)
            ry = y
            rz = x * Math.sin(currentRad) + z * Math.cos(currentRad)
        } else if (axis === 'Z') {
            // Axe Z Métier (Vertical)
            // Pivotement horizontal à plat (Yaw) : tourne dans le plan X-Y métier
            // Échange Longueur (L / x SVG) ↔ Largeur (W / y SVG)
            rx = x * Math.cos(currentRad) - y * Math.sin(currentRad)
            ry = x * Math.sin(currentRad) + y * Math.cos(currentRad)
            rz = z
        }

        return projectStatic(rx, ry, rz)
    }

    // 8 vertices of the box centered around (0, 0, 0)
    const halfL = l / 2
    const halfW = w / 2
    const minZ = -h / 2
    const maxZ = h / 2

    const vertices = [
        [-halfL, -halfW, minZ], // 0: Front-Left-Bottom
        [halfL, -halfW, minZ],  // 1: Front-Right-Bottom
        [halfL, halfW, minZ],   // 2: Back-Right-Bottom
        [-halfL, halfW, minZ],  // 3: Back-Left-Bottom
        [-halfL, -halfW, maxZ], // 4: Front-Left-Top
        [halfL, -halfW, maxZ],  // 5: Front-Right-Top
        [halfL, halfW, maxZ],   // 6: Back-Right-Top
        [-halfL, halfW, maxZ],  // 7: Back-Left-Top
    ] as [number, number, number][]

    // Project all vertices
    const projVerts = vertices.map(v => project3D(v[0], v[1], v[2]))

    // 6 faces of the box
    const faces = [
        { name: 'bottom', indices: [0, 1, 2, 3], shade: 0.6 },
        { name: 'top', indices: [4, 5, 6, 7], shade: 1.15 },
        { name: 'front', indices: [0, 1, 5, 4], shade: 0.95 },
        { name: 'right', indices: [1, 2, 6, 5], shade: 0.8 },
        { name: 'back', indices: [2, 3, 7, 6], shade: 0.65 },
        { name: 'left', indices: [3, 0, 4, 7], shade: 0.75 },
    ]

    // Calculate face depths and sort for painter's algorithm
    const sortedFaces = faces.map(f => {
        const avgDepth = f.indices.reduce((sum, idx) => sum + projVerts[idx][2], 0) / f.indices.length
        return { ...f, avgDepth }
    }).sort((a, b) => a.avgDepth - b.avgDepth)

    // ── Render Static 3D Axis Arrow Guide (fixe, indépendant de la rotation) ──
    // Convention: X=Longitudinal(x SVG), Y=Latéral(y SVG), Z=Vertical(z SVG)
    const axisLength = maxDim * 1.15
    const axisStart = projectStatic(
        axis === 'X' ? -axisLength : 0,
        axis === 'Y' ? -axisLength : 0,
        axis === 'Z' ? -axisLength : 0
    )
    const axisEnd = projectStatic(
        axis === 'X' ? axisLength : 0,
        axis === 'Y' ? axisLength : 0,
        axis === 'Z' ? axisLength : 0
    )

    return (
        <div
            className={`relative rounded-2xl border transition-all duration-300 overflow-hidden flex flex-col justify-between ${isAxisActive
                ? 'bg-gray-100 dark:bg-slate-900/90 border-gray-300 dark:border-slate-700 hover:border-gray-400 dark:hover:border-slate-500 shadow-xl'
                : 'bg-gray-50 dark:bg-slate-950/60 border-gray-200 dark:border-slate-800/80 opacity-75 grayscale-[30%]'
                }`}
        >
            {/* Header Accent Bar */}
            <div className={`h-1 w-full bg-gradient-to-r ${isAxisActive
                ? axis === 'X' ? 'from-red-500 to-amber-500' : axis === 'Y' ? 'from-emerald-500 to-teal-500' : 'from-blue-500 to-indigo-500'
                : 'from-gray-400 dark:from-slate-700 to-gray-500 dark:to-slate-800'
                }`} />

            {/* Top Info Header */}
            <div className="p-3.5 pb-0 flex items-start justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <span
                            className="w-5 h-5 rounded-md flex items-center justify-center font-black text-xs text-white shadow-sm"
                            style={{ backgroundColor: axisColorHex }}
                        >
                            {axis}
                        </span>
                        <h4 className="font-bold text-sm text-gray-900 dark:text-white tracking-tight">{title}</h4>
                    </div>
                </div>

                {/* Toggle Switch */}
                <button
                    type="button"
                    onClick={onToggle}
                    disabled={!rotatable}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider transition-all ${isAxisActive
                        ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/30'
                        : 'bg-gray-200 dark:bg-slate-800 text-gray-500 dark:text-slate-400 border border-gray-300 dark:border-slate-700 hover:bg-gray-300 dark:hover:bg-slate-700'
                        }`}
                >
                    {isAxisActive ? (
                        <><CheckCircle2 className="w-3 h-3 text-emerald-600 dark:text-emerald-400" /> Autorisé</>
                    ) : (
                        <><Lock className="w-3 h-3 text-gray-500 dark:text-slate-400" /> Verrouillé</>
                    )}
                </button>
            </div>

            {/* 3D Visual Canvas Box Area */}
            <div className="relative my-2 h-44 flex items-center justify-center overflow-hidden bg-gray-100 dark:bg-slate-950/40 rounded-xl border border-gray-300 dark:border-slate-800/50 mx-3">
                {/* Ambient Grid Background */}
                <div
                    className="absolute inset-0 opacity-15"
                    style={{
                        backgroundImage: `radial-gradient(${axisColorHex} 1px, transparent 1px)`,
                        backgroundSize: '16px 16px',
                    }}
                />

                {/* Axis Glow Light */}
                <div
                    className="absolute w-32 h-32 rounded-full blur-2xl opacity-20 pointer-events-none"
                    style={{ backgroundColor: isAxisActive ? axisColorHex : (theme === 'dark' ? '#64748b' : '#9ca3af') }}
                />

                {/* Interactive Controls overlay */}
                {isAxisActive && (
                    <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-gray-200/80 dark:bg-slate-900/80 backdrop-blur-md p-1 rounded-lg border border-gray-300 dark:border-slate-700/60 shadow-lg">
                        <button
                            type="button"
                            title={isPlaying ? 'Pause simulation' : 'Jouer simulation 90°'}
                            onClick={() => setIsPlaying(!isPlaying)}
                            className="p-1 rounded text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-300 dark:hover:bg-slate-800 transition-colors"
                        >
                            {isPlaying ? <Pause className="w-3.5 h-3.5 text-amber-400" /> : <Play className="w-3.5 h-3.5 text-emerald-400" />}
                        </button>
                        <button
                            type="button"
                            title="Bascule manuelle 90°"
                            onClick={() => {
                                setIsPlaying(false)
                                setAngleDeg(prev => (prev === 0 ? 90 : 0))
                            }}
                            className="p-1 rounded text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-300 dark:hover:bg-slate-800 transition-colors"
                        >
                            <RotateCw className="w-3.5 h-3.5 text-blue-400" />
                        </button>
                    </div>
                )}

                {/* SVG 3D Canvas */}
                <svg width={svgWidth} height={svgHeight} className="overflow-visible select-none z-0">
                    <defs>
                        <filter id={`glow-${axis}`} x="-20%" y="-20%" width="140%" height="140%">
                            <feGaussianBlur stdDeviation="3" result="blur" />
                            <feComposite in="SourceGraphic" in2="blur" operator="over" />
                        </filter>
                        <linearGradient id={`grad-shadow-${axis}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#000000" stopOpacity="0.4" />
                            <stop offset="100%" stopColor="#000000" stopOpacity="0" />
                        </linearGradient>
                    </defs>

                    {/* Ground Shadow Ellipse */}
                    <ellipse
                        cx={cx}
                        cy={cy + (maxZ * scale * Math.cos(camPitch)) + 12}
                        rx={halfL * scale * 1.4}
                        ry={halfW * scale * 0.7}
                        fill={`url(#grad-shadow-${axis})`}
                    />

                    {/* Render 3D Box Faces */}
                    {sortedFaces.map(f => {
                        const pts = f.indices.map(idx => `${projVerts[idx][0].toFixed(1)},${projVerts[idx][1].toFixed(1)}`).join(' ')
                        const faceColor = adjustBrightness(color, f.shade)
                        const inactiveColor = theme === 'dark' ? '#475569' : '#9ca3af'

                        return (
                            <polygon
                                key={f.name}
                                points={pts}
                                fill={isAxisActive ? faceColor : adjustBrightness(inactiveColor, f.shade)}
                                stroke={isAxisActive ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.2)') : (theme === 'dark' ? 'rgba(148, 163, 184, 0.2)' : 'rgba(0, 0, 0, 0.1)')}
                                strokeWidth="1.2"
                                strokeLinejoin="round"
                                opacity={isAxisActive ? 0.95 : 0.7}
                            />
                        )
                    })}

                    {/* 3D Static Central Axis Line Guide Overlay */}
                    <line
                        x1={axisStart[0]}
                        y1={axisStart[1]}
                        x2={axisEnd[0]}
                        y2={axisEnd[1]}
                        stroke={axisColorHex}
                        strokeWidth={isAxisActive ? "3" : "1.5"}
                        strokeDasharray={isAxisActive ? "" : "3 3"}
                        opacity={isAxisActive ? 0.95 : 0.5}
                        filter={isAxisActive ? `url(#glow-${axis})` : undefined}
                    />
                    <circle
                        cx={axisEnd[0]}
                        cy={axisEnd[1]}
                        r={4}
                        fill={axisColorHex}
                        stroke="#ffffff"
                        strokeWidth="1"
                        opacity={isAxisActive ? 1 : 0.6}
                    />
                    <circle
                        cx={axisStart[0]}
                        cy={axisStart[1]}
                        r={3}
                        fill={axisColorHex}
                        opacity={isAxisActive ? 0.8 : 0.4}
                    />

                    {/* Rotation Arc Arrow Overlay when Axis Active */}
                    {isAxisActive && (
                        <g transform={`translate(${cx}, ${cy - 10})`}>
                            <path
                                d="M -25 -5 A 30 20 0 0 1 25 -5"
                                fill="none"
                                stroke={axisColorHex}
                                strokeWidth="2.5"
                                strokeDasharray="4 2"
                                className={isPlaying ? "animate-pulse" : ""}
                                filter={`url(#glow-${axis})`}
                            />
                            <polygon
                                points="23,-10 30,-5 23,0"
                                fill={axisColorHex}
                            />
                        </g>
                    )}

                    {/* Locked Watermark Cross when disabled */}
                    {!isAxisActive && (
                        <g transform={`translate(${cx}, ${cy})`} opacity={0.6}>
                            <circle r="18" fill={theme === 'dark' ? '#0f172a' : '#e5e7eb'} stroke={theme === 'dark' ? '#64748b' : '#9ca3af'} strokeWidth="1.5" />
                            <Lock className="w-5 h-5 text-gray-500 dark:text-slate-400 -translate-x-2.5 -translate-y-2.5" />
                        </g>
                    )}
                </svg>

                {/* Current Angle Badge */}
                {isAxisActive && (
                    <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded bg-gray-200/80 dark:bg-slate-900/80 border border-gray-300 dark:border-slate-700/80 text-[10px] font-mono text-gray-600 dark:text-slate-300 flex items-center gap-1 shadow-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 dark:bg-emerald-400 animate-pulse" />
                        Orientation: <strong className="text-gray-900 dark:text-white font-bold">{Math.round(angleDeg)}°</strong>
                    </div>
                )}
            </div>

            {/* Footer Dimension Specs & Description */}
            <div className="p-3 pt-1 space-y-2 bg-gray-100 dark:bg-slate-900/60 border-t border-gray-300 dark:border-slate-800/80">
                <p className="text-[11px] text-gray-600 dark:text-slate-300 leading-tight">
                    {description}
                </p>

                {/* Dimensions Comparison Box */}
                <div className="grid grid-cols-2 gap-1.5 pt-1 text-[10px] font-mono">
                    <div className="bg-white dark:bg-slate-950/80 p-1.5 rounded-lg border border-gray-300 dark:border-slate-800 text-gray-500 dark:text-slate-400">
                        <span className="block text-[9px] uppercase tracking-wider font-sans text-gray-400 dark:text-slate-500">Initiale (0°)</span>
                        <span className="font-bold text-gray-700 dark:text-slate-200"><Dimensions length={l} width={w} height={h} from={unit} /></span>
                    </div>
                    <div className={`p-1.5 rounded-lg border transition-colors ${isAxisActive
                        ? 'bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-200'
                        : 'bg-gray-50 dark:bg-slate-950/40 border-gray-200 dark:border-slate-800/50 text-gray-500 dark:text-slate-500'
                        }`}>
                        <span className="block text-[9px] uppercase tracking-wider font-sans text-gray-400 dark:text-slate-500">Inversée (90°)</span>
                        <span className={`font-bold ${isAxisActive ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-slate-500'}`}>
                            <Dimensions length={rotatedDims.l} width={rotatedDims.w} height={rotatedDims.h} from={unit} />
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}

// ── Main Exported Component ──────────────────────────────────────────────────
export default function ProductRotation3DPreview({
    length,
    width,
    height,
    unit = 'cm',
    color = '#6366f1',
    rotatable,
    allowRotationX,
    allowRotationY,
    allowRotationZ,
    onToggleAxis,
}: ProductRotation3DPreviewProps) {
    // Use real dimensions or fallback defaults
    const l = length && length > 0 ? length : 120
    const w = width && width > 0 ? width : 80
    const h = height && height > 0 ? height : 100

    return (
        <div className="space-y-4">
            {/* Grid of 3 Axis Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* AXE X (ROLL autour de l'axe longitudinal) - Convention Three.js */}
                <SingleAxis3DCard
                    axis="X"
                    title="Axe X (Roll Longitudinal)"
                    subtitle="Bascule sur le flanc (gauche/droite)"
                    description="Rotation autour de l'axe longitudinal. Échange la Largeur (W) et la Hauteur (H)."
                    enabled={allowRotationX}
                    rotatable={rotatable}
                    l={l}
                    w={w}
                    h={h}
                    unit={unit}
                    color={color}
                    onToggle={() => onToggleAxis?.('allow_rotation_x', !allowRotationX)}
                />

                {/* AXE Y (PITCH autour de l'axe latéral) */}
                <SingleAxis3DCard
                    axis="Y"
                    title="Axe Y (Pitch Latéral)"
                    subtitle="Bascule Avant / Arrière"
                    description="Rotation autour de l'axe transversal (latéral). Échange la Longueur (L) et la Hauteur (H)."
                    enabled={allowRotationY}
                    rotatable={rotatable}
                    l={l}
                    w={w}
                    h={h}
                    unit={unit}
                    color={color}
                    onToggle={() => onToggleAxis?.('allow_rotation_y', !allowRotationY)}
                />

                {/* AXE Z (YAW autour de l'axe vertical) */}
                <SingleAxis3DCard
                    axis="Z"
                    title="Axe Z (Yaw Vertical)"
                    subtitle="Pivotement horizontal à plat"
                    description="Rotation autour de l'axe vertical. Échange la Longueur (L) et la Largeur (W)."
                    enabled={allowRotationZ}
                    rotatable={rotatable}
                    l={l}
                    w={w}
                    h={h}
                    unit={unit}
                    color={color}
                    onToggle={() => onToggleAxis?.('allow_rotation_z', !allowRotationZ)}
                />
            </div>
        </div>
    )
}
